import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  Box,
  Button,
  ButtonBase,
  Collapse,
  Container,
  Grid,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  SxProps,
  Tooltip,
  Typography,
  TypographyProps,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { MoreVert as MoreVertIcon, Add as AddIcon } from '@mui/icons-material';

import { Moment } from 'moment';
import { LoadingButton } from '@mui/lab';
import { useAppSelector } from '../../../../hooks';
import { checkUserPermission } from '../../../../helper';

import { TCourseStatus } from '../../../../store/V2/CoursesSlice';
import { getCurriculumsV2 } from '../../../../store/V2/CurriculumSlice';
import { getAuthState } from '../../../../store/AuthSlice';
import {
  getTrialsForAdmin,
  loadMoreTrialsForAdmin,
} from '../../../../store/V2/TrialsSlice/adminApi';
import {
  getTrialsState,
  ITrialClass,
  resetAdminTrials,
} from '../../../../store/V2/TrialsSlice';

import Breadcrumbs, {
  BreadcrumbOption,
} from '../../../../components/BreadCrumb/Breadcrumbs';
import AdminTrialClassListItem, {
  TActionTypes,
} from './components/AdminTrialClassListItem';
import BasicLoader from '../../../../components/Loaders/BasicLoader';
import FilterSection from './components/FilterSection';
import AdminBookTrialModal from './modals/AdminBookTrialModal';
import StatusChangeModal from './modals/StatusChangeModal';
import { USER_PERMISSION_INDIVIDUALS } from '../../../../store/V2/RolesSlice';

const titleProps: TypographyProps = {
  color: '#F8FAFC',
  fontWeight: '700',
  fontSize: '30px',
  textAlign: 'center',
};

const boxStyles: SxProps = {
  background: `url('${process.env.PUBLIC_URL}/images/trial/bg-banner.png') top right/contain no-repeat, linear-gradient(107.83deg, #003768 7.88%, #006C9C 41.91%, #39BFB9 82.16%)`,
  paddingBottom: '42px',
  marginBottom: '30px',
};

const constructBreadcrumbOptions = () => {
  const options: Array<BreadcrumbOption> = [
    {
      type: 'link',
      title: 'Home',
      link: `${process.env.PUBLIC_URL}`,
    },
    {
      type: 'text',
      title: 'Trial Classes',
      highlight: true,
    },
  ];

  return options;
};

type FilterData = {
  limit: number;
  offset: number;
  curriculum?: string;
  subject?: string;
  grade?: string;
  studentId?: string;
  tutorId?: string;
  status?: TCourseStatus;
  startDate?: Moment | null;
  endDate?: Moment | null;
};

type TState = {
  filterData: FilterData;
  anchorEl: HTMLElement | null;
  isBookTrialModalOpen: boolean;
  isFiltersVisible: boolean;
  isStatusModalOpen: boolean;
  selectedTrial?: ITrialClass;
};

export const initialFilterData: FilterData = {
  limit: 30,
  offset: 0,
  startDate: null,
  endDate: null,
};

const initialState: TState = {
  filterData: { ...initialFilterData },
  anchorEl: null,
  isBookTrialModalOpen: false,
  isFiltersVisible: false,
  isStatusModalOpen: false,
};

const AdminTrialClasses: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isXsScreen = useMediaQuery(theme.breakpoints.only('xs'));

  // Store
  const { adminTrials, status } = useAppSelector(getTrialsState);
  const { user: authUser } = useAppSelector(getAuthState);

  // State
  const [state, setState] = useState(initialState);

  const isMoreOptionsMenuOpen = Boolean(state.anchorEl);
  const breadcrumbOptions = constructBreadcrumbOptions();

  useEffect(() => {
    dispatch(getCurriculumsV2());
    dispatch(getTrialsForAdmin(state.filterData));

    return () => {
      dispatch(resetAdminTrials());
    };
  }, []);

  const toggleFilters = () => {
    setState((prevState) => ({
      ...prevState,
      isFiltersVisible: !prevState.isFiltersVisible,
    }));
  };

  const updateFilterData = (attributes: Partial<FilterData>) => {
    setState((prevState) => {
      const newFilterData = {
        ...prevState.filterData,
        ...attributes,
      };

      dispatch(getTrialsForAdmin(newFilterData));

      return {
        ...prevState,
        filterData: newFilterData,
      };
    });
  };

  const openMoreOptionsMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setState((prevState) => ({
      ...prevState,
      anchorEl: event.currentTarget,
    }));
  };

  const closeMoreOptionsMenu = () => {
    setState((prevState) => ({
      ...prevState,
      anchorEl: null,
    }));
  };

  const toggleBookTrialModal = () => {
    setState((prevState) => ({
      ...prevState,
      isBookTrialModalOpen: !prevState.isBookTrialModalOpen,
    }));
  };

  const toggleStatusModal = () => {
    setState((prevState) => ({
      ...prevState,
      isStatusModalOpen: !prevState.isStatusModalOpen,
    }));
  };

  const handleActions = (action: TActionTypes, trialData: ITrialClass) => {
    setState((prevState) => ({
      ...prevState,
      selectedTrial: trialData,
    }));
    if (action === 'edit') {
      toggleBookTrialModal();
    } else {
      toggleStatusModal();
    }
  };

  const handleLoadMore = () => {
    if (adminTrials) {
      const newFilterData = {
        ...state.filterData,
        offset: adminTrials.list.length,
      };

      setState((prevState) => ({
        ...prevState,
        filterData: newFilterData,
      }));

      dispatch(loadMoreTrialsForAdmin(newFilterData));
    }
  };

  const refreshTrials = () => {
    dispatch(getTrialsForAdmin(state.filterData));
  };

  const moreOptionsMenuOptions = [
    {
      icon: AddIcon,
      title: 'Book a new Trial',
      onClick: () => {
        setState((prevState) => ({
          ...prevState,
          selectedTrial: undefined,
        }));
        toggleBookTrialModal();
        closeMoreOptionsMenu();
      },
    },
  ];

  return (
    <>
      <Box sx={boxStyles}>
        <Container maxWidth="lg">
          <div style={{ padding: `30px 0 ${isXsScreen ? '15px' : '0'} 0` }}>
            <Breadcrumbs
              options={breadcrumbOptions}
              showDropDown={isXsScreen}
            />
          </div>
          <Typography {...titleProps}>Trial Classes</Typography>
        </Container>
      </Box>
      <Container maxWidth="md">
        <Collapse in={state.isFiltersVisible}>
          <FilterSection updateFilterState={updateFilterData} />
        </Collapse>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Button variant="text" color="info" onClick={toggleFilters}>
            {state.isFiltersVisible ? 'Hide Filters' : 'Show Filters'}
          </Button>
          {checkUserPermission(authUser, {
            adminTrialClass: [
              USER_PERMISSION_INDIVIDUALS.ADMIN_TRIAL_CLASS_CREATE,
            ],
          }) ? (
            <Tooltip title="Options" placement="top">
              <ButtonBase
                id="more-options-button"
                aria-haspopup="true"
                onClick={openMoreOptionsMenu}
                aria-expanded={isMoreOptionsMenuOpen ? 'true' : undefined}
                aria-controls={
                  isMoreOptionsMenuOpen ? 'more-options-menu' : undefined
                }
                sx={{
                  backgroundColor: '#F1F5F9',
                  padding: '5px',
                  borderRadius: '5px',
                }}
              >
                <MoreVertIcon />
              </ButtonBase>
            </Tooltip>
          ) : null}
        </Stack>

        <Stack marginTop="20px">
          {!adminTrials && status === 'loading' ? (
            <Grid item xs={12}>
              <BasicLoader />
            </Grid>
          ) : null}
          {adminTrials && (adminTrials.list || []).length
            ? adminTrials.list.map((trialItem, index) => (
                <AdminTrialClassListItem
                  key={index}
                  trialData={trialItem}
                  handleActions={handleActions}
                />
              ))
            : null}
          {adminTrials &&
            (adminTrials.totalDocs || 0) > adminTrials.list.length && (
              <Stack direction="row" justifyContent="center">
                <LoadingButton
                  loading={status === 'loading'}
                  disabled={status === 'loading'}
                  onClick={handleLoadMore}
                >
                  Load More
                </LoadingButton>
              </Stack>
            )}
          {adminTrials &&
          adminTrials.list &&
          adminTrials.list.length < 1 &&
          status === 'idle' ? (
            <Typography variant="h6" textAlign="center">
              No Trial classes found
            </Typography>
          ) : null}
        </Stack>
      </Container>
      {/* Modals */}
      {state.isBookTrialModalOpen && (
        <AdminBookTrialModal
          open={state.isBookTrialModalOpen}
          trialData={state.selectedTrial}
          onClose={toggleBookTrialModal}
        />
      )}
      {state.isStatusModalOpen && state.selectedTrial && (
        <StatusChangeModal
          open={state.isStatusModalOpen}
          trialData={state.selectedTrial}
          onClose={toggleStatusModal}
          refreshList={refreshTrials}
        />
      )}
      <Menu
        open={isMoreOptionsMenuOpen}
        id="more-options-menu"
        anchorEl={state.anchorEl}
        onClose={closeMoreOptionsMenu}
        MenuListProps={{
          'aria-labelledby': 'more-options-button',
        }}
      >
        {moreOptionsMenuOptions.map((item, index) => {
          const Icon = item.icon;

          return (
            <MenuItem
              key={`more-option-menu-li-${index + 1}`}
              onClick={item.onClick}
            >
              <ListItemIcon>
                <Icon />
              </ListItemIcon>
              <ListItemText>{item.title}</ListItemText>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default AdminTrialClasses;
